import React, { useState } from 'react';
import apiService, { errorHelpers, validationHelpers } from '../../utils/apiService';

const ForgotPasswordModal = ({ onClose, onBackToLogin }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [emailSent, setEmailSent] = useState(false);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setAlert(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setAlert({
                type: 'error',
                message: 'Email address is required'
            });
            return;
        }

        if (!validationHelpers.validateEmail(email)) {
            setAlert({
                type: 'error',
                message: 'Please enter a valid email address'
            });
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.auth.forgotPassword(email);

            setAlert({
                type: 'success',
                message: response.data.message
            });

            setEmailSent(true);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleResendEmail = () => {
        setEmailSent(false);
        setAlert(null);
    };

    return (
        <div className="modal-overlay" onClick={handleOverlayClick}>
            <div className="modal">
                <div className="modal-header">
                    <h2 className="modal-title">
                        {emailSent ? 'Check Your Email' : 'Forgot Password'}
                    </h2>
                    <button className="modal-close" onClick={onClose} type="button">
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    {!emailSent ? (
                        <>
                            <p className="modal-description">
                                Enter your email address below and we'll send you a link to reset your password.
                            </p>

                            {alert && (
                                <div className={`alert alert-${alert.type}`}>
                                    {alert.message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="email">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        className="form-input"
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder="Enter your email address"
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary btn-block"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="loading-spinner mr-2"></span>
                                            Sending Reset Link...
                                        </>
                                    ) : (
                                        <>
                                            <span className="mr-2">📧</span>
                                            Send Reset Link
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="modal-footer-links">
                                <button
                                    type="button"
                                    className="link-button"
                                    onClick={onBackToLogin}
                                    disabled={loading}
                                >
                                    ← Back to Login
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="email-sent-content">
                            <div className="success-icon">📧</div>

                            <h3>Reset Link Sent!</h3>

                            <p className="success-message">
                                If an account with the email address <strong>{email}</strong> exists,
                                you will receive a password reset link shortly.
                            </p>

                            <div className="email-instructions">
                                <h4>What to do next:</h4>
                                <ol>
                                    <li>Check your email inbox</li>
                                    <li>Look for an email from OOU Certificate System</li>
                                    <li>Click the reset link in the email</li>
                                    <li>Follow the instructions to reset your password</li>
                                </ol>
                            </div>

                            <div className="email-help">
                                <p className="help-text">
                                    <strong>Didn't receive the email?</strong>
                                </p>
                                <ul className="help-list">
                                    <li>Check your spam/junk folder</li>
                                    <li>Make sure you entered the correct email address</li>
                                    <li>Wait a few minutes as emails may be delayed</li>
                                </ul>
                            </div>

                            <div className="email-actions">
                                <button
                                    onClick={handleResendEmail}
                                    className="btn btn-secondary"
                                    disabled={loading}
                                >
                                    Try Different Email
                                </button>
                                <button
                                    onClick={onBackToLogin}
                                    className="btn btn-primary"
                                >
                                    Back to Login
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Styles */}
            <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2rem 2rem 1rem;
          border-bottom: 1px solid var(--gray-200);
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--gray-500);
          padding: 0.25rem;
          line-height: 1;
          border-radius: 0.25rem;
          transition: all 0.3s ease;
        }

        .modal-close:hover {
          color: var(--gray-700);
          background: var(--gray-100);
        }

        .modal-body {
          padding: 1.5rem 2rem 2rem;
        }

        .modal-description {
          color: var(--gray-600);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        .form-input {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid var(--gray-200);
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--secondary-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .btn {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-block {
          width: 100%;
        }

        .btn-secondary {
          background: white;
          color: var(--primary-blue);
          border: 2px solid var(--primary-blue);
        }

        .btn-secondary:hover:not(:disabled) {
          background: var(--primary-blue);
          color: white;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .mr-2 {
          margin-right: 0.5rem;
        }

        .modal-footer-links {
          text-align: center;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--gray-200);
        }

        .link-button {
          background: none;
          border: none;
          color: var(--secondary-blue);
          cursor: pointer;
          font-size: 1rem;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .link-button:hover:not(:disabled) {
          color: var(--primary-blue);
          text-decoration: underline;
        }

        .link-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .alert {
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid transparent;
        }

        .alert-success {
          background: var(--green-50);
          color: var(--success-green);
          border-color: var(--green-200);
        }

        .alert-error {
          background: var(--red-50);
          color: var(--error-red);
          border-color: var(--red-200);
        }

        /* Email sent content styles */
        .email-sent-content {
          text-align: center;
        }

        .success-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
        }

        .email-sent-content h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 1rem;
        }

        .success-message {
          font-size: 1rem;
          color: var(--gray-600);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .email-instructions {
          background: var(--blue-50);
          border: 1px solid var(--blue-200);
          border-radius: 0.75rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }

        .email-instructions h4 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--blue-800);
          margin-bottom: 1rem;
        }

        .email-instructions ol {
          color: var(--blue-700);
          margin: 0;
          padding-left: 1.5rem;
        }

        .email-instructions li {
          margin-bottom: 0.5rem;
        }

        .email-help {
          background: var(--gray-50);
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }

        .help-text {
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 1rem;
        }

        .help-list {
          color: var(--gray-600);
          margin: 0;
          padding-left: 1.5rem;
        }

        .help-list li {
          margin-bottom: 0.5rem;
        }

        .email-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .email-actions .btn {
          flex: 1;
          min-width: 140px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 640px) {
          .modal {
            margin: 1rem;
            max-width: none;
          }

          .modal-header {
            padding: 1.5rem 1.5rem 1rem;
          }

          .modal-body {
            padding: 1rem 1.5rem 1.5rem;
          }

          .email-actions {
            flex-direction: column;
          }

          .email-actions .btn {
            flex: none;
            width: 100%;
          }
        }
      `}</style>
        </div>
    );
};

export default ForgotPasswordModal;